/** Pendant zu `Services/API/ApiGroupService.cs`. */

import type { ChinaAppSysGroup, ChinaAppSysGroupView, ChinaAppSysUser } from '@/types/entities';

import {
  buildAppPath,
  createCrudApi,
  requestOrFallback,
  requestSucceeded,
  type CrudApi,
} from '../apiBase';
import { apiClient } from '../httpClient';

const ENDPOINT = 'groups';

/**
 * Rumpf von `POST groups/{id}/views/add` und `PUT groups/{id}/views/{groupViewId}` ohne die
 * Navigationsobjekte, die `GET groups/{id}/views` mitliefert (`Include(x => x.View)`).
 *
 * Der PUT ist ein Voll-Update (`CurrentValues.SetValues`): ein ausgelassenes skalares Feld käme
 * als JSON-Vorgabe an und überschriebe den Stand in der Datenbank — der Rumpf muss sie also alle
 * enthalten. `View` und `Group` setzt der Server dagegen selbst (`PostAddView`); mitgesendet
 * beschäftigten sie nur die Modellbindung.
 */
export type ChinaAppSysGroupViewBody = Omit<
  ChinaAppSysGroupView,
  'Group' | 'GroupBezeichnung' | 'View' | 'ViewBezeichnung'
>;

export const groupApi: CrudApi<ChinaAppSysGroup> & {
  getUsers(id: number): Promise<ChinaAppSysUser[]>;
  addUser(id: number, userId: number): Promise<boolean>;
  removeUser(id: number, userId: number): Promise<boolean>;
  getViews(id: number): Promise<ChinaAppSysGroupView[]>;
  addView(id: number, view: ChinaAppSysGroupViewBody): Promise<boolean>;
  putView(id: number, groupViewId: number, view: ChinaAppSysGroupViewBody): Promise<boolean>;
  removeView(id: number, viewId: number): Promise<boolean>;
} = {
  ...createCrudApi<ChinaAppSysGroup>(ENDPOINT),

  getUsers: (id) =>
    requestOrFallback(
      () => apiClient.get<ChinaAppSysUser[]>(`${buildAppPath(ENDPOINT)}/${id}/users`),
      [],
    ),

  // Wie bei den Gruppen eines Benutzers erwartet die Verknüpfungsroute keinen Rumpf.
  addUser: (id, userId) =>
    requestSucceeded(() =>
      apiClient.raw('POST', `${buildAppPath(ENDPOINT)}/${id}/users/add/${userId}`),
    ),

  removeUser: (id, userId) =>
    requestSucceeded(() =>
      apiClient.raw('DELETE', `${buildAppPath(ENDPOINT)}/${id}/users/remove/${userId}`),
    ),

  getViews: (id) =>
    requestOrFallback(
      () => apiClient.get<ChinaAppSysGroupView[]>(`${buildAppPath(ENDPOINT)}/${id}/views`),
      [],
    ),

  addView: (id, view) =>
    requestSucceeded(() =>
      apiClient.raw('POST', `${buildAppPath(ENDPOINT)}/${id}/views/add`, view),
    ),

  putView: (id, groupViewId, view) =>
    requestSucceeded(() =>
      apiClient.raw('PUT', `${buildAppPath(ENDPOINT)}/${id}/views/${groupViewId}`, view),
    ),

  removeView: (id, viewId) =>
    requestSucceeded(() =>
      apiClient.raw('DELETE', `${buildAppPath(ENDPOINT)}/${id}/views/remove/${viewId}`),
    ),
};
